import React, { useEffect, useRef, useState } from 'react';
import { Button, NameEmailAvatar } from '../../../../../../../ui';
import { ChevronDown, ChevronUp } from '../../../../../../../ui/icons/icons';
import {
  addPanelistNotes,
  getInterviewApplicant,
  getInterviewRound,
} from '../../../../../../../utils/api_service/SmartInterview';

interface IPanelistNotes {
  open: boolean;
  handleClosePanelistNotes: () => void;
  intervieweeName?: string;
  email?: string;
  interviewRoundId: string;
  applicantId: string;
  onSuccess?: () => void;
}

export const PanelistNotesDialog = ({
  open,
  handleClosePanelistNotes,
  email,
  intervieweeName,
  interviewRoundId,
  applicantId,
  onSuccess,
}: IPanelistNotes) => {
  const [newNote, setNewNote] = useState<string>('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState<string>('');
  const [isPanelist, setIsPanelist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingNotes, setExistingNotes] = useState<
    Array<{
      id: number;
      createdAt: string;
      panelist: string;
      notes: string;
    }>
  >([]);
  const [panelistsMap, setPanelistsMap] = useState<Record<string, any>>({});
  const [userPanelistId, setUserPanelistId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const userId = localStorage.getItem('id') || '';

  // Fetch interview round penelists and check if current user is a Penalist
  useEffect(() => {
    if (!interviewRoundId) return;

    const fetchRoundInfo = async () => {
      try {
        const response = await getInterviewRound(interviewRoundId);
        if (!response.ok) throw new Error(`Status ${response.status}`);
        const round = await response.json();

        const penelists: Record<string, any> = {};
        let foundPenalistId: string | null = null;

        // Check if the user is in the panelists array
        const panelists = round?.panelists || [];
        panelists.forEach((p: any) => {
          if (p?.user?.id) {
            penelists[p.id] = {
              firstName: p.user.firstName,
              lastName: p.user.lastName,
              email: p.user.email,
              avatar: p.user.profilePic || '',
            };

            // Check if this panelist is the current user
            if (p.user.id === userId) {
              foundPenalistId = p.id;
            }
          }
        });

        setPanelistsMap(penelists);
        setUserPanelistId(foundPenalistId);
        setIsPanelist(foundPenalistId !== null);
        // Debug info to help troubleshoot panelist detection
        setDebugInfo({
          userId,
          foundPanelist: foundPenalistId !== null,
          panelists: round?.penelists,
          penelists,
        });
      } catch (err) {
        setPanelistsMap({});
        setUserPanelistId(null);
        setIsPanelist(false);
      }
    };

    fetchRoundInfo();
  }, [interviewRoundId, userId]);

  // Fetch existing Penalist notes for the applicant
  useEffect(() => {
    if (!open || !interviewRoundId || !applicantId) return;

    const fetchNotes = async () => {
      try {
        const res = await getInterviewApplicant(applicantId, interviewRoundId);
        const result = await res.json();

        let notes = [];
        if (result?.PenalistNotes) {
          notes = result.PenalistNotes;
        } else if (result?.panelistNotes) {
          notes = result.panelistNotes;
        } else {
          console.log('No notes found in response:', result);
        }

        setExistingNotes(notes);

        // Check if the current user has already added a note
        const userNote = notes.find(
          (note: { id: number; panelist: string; notes: string }) =>
            (note.panelist || '').trim() === userPanelistId,
        );

        // If user has a note, prepare it for potential editing
        if (userNote) {
          setEditingNoteId(userNote.id);
          setEditingNoteText(userNote.notes);
          // Don't show the add note form if user already has a note
          setExpanded(false);
        } else {
          // Show the add note form if the user doesn't have an existing note
          setExpanded(true);
          setEditingNoteId(null);
          setEditingNoteText('');
        }
      } catch (err) {
        setExistingNotes([]);
        setExpanded(true); // Show add note form if we can't fetch existing notes
      }
    };

    fetchNotes();
  }, [open, applicantId, interviewRoundId, userPanelistId]);

  const handleSave = async () => {
    if (!isPanelist || loading) {
      return;
    }

    const isNewNote = editingNoteId === null;
    const noteToSave = isNewNote ? newNote : editingNoteText;

    // Validate notes content
    if (!noteToSave.trim()) {
      setError('Please enter some notes before saving');
      return;
    }

    // For edit mode, check if there are actual changes
    if (!isNewNote) {
      const originalNote = existingNotes.find(
        (note) => note.id === editingNoteId,
      );
      if (originalNote && originalNote.notes.trim() === noteToSave.trim()) {
        // No changes made, just close dialog
        setEditingNoteId(null);
        setEditingNoteText('');
        setExpanded(false);
        handleClosePanelistNotes();
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // Create a new note object
      const newNoteObj = {
        id: isNewNote ? Date.now() : editingNoteId, // Use timestamp as temporary ID for new notes
        notes: noteToSave,
        panelist: userPanelistId || '',
        createdAt: isNewNote
          ? new Date().toISOString()
          : existingNotes.find((n) => n.id === editingNoteId)?.createdAt ||
            new Date().toISOString(),
      };

      // Update the UI immediately with optimistic update
      if (isNewNote) {
        setExistingNotes((prev) => [newNoteObj, ...prev]); // Add new note at the beginning
      } else {
        setExistingNotes((prev) =>
          prev.map((note) =>
            note.id === editingNoteId ? { ...note, notes: noteToSave } : note,
          ),
        );
      }

      // Save to backend
      const response = await addPanelistNotes(
        interviewRoundId,
        applicantId,
        noteToSave,
      );
      // Reset the form
      setNewNote('');
      setEditingNoteId(null);
      setEditingNoteText('');
      setExpanded(false);

      onSuccess?.();

      // Close dialog after successful save
      handleClosePanelistNotes();
    } catch (err) {
      console.error('Error saving notes:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to save notes. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything if the dialog is not open
  if (!open) return null;

  // Get user note status
  const hasUserNote = existingNotes.some(
    (note) => (note.panelist || '').trim() === userPanelistId,
  );

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-auto'>
      <div className='relative bg-white rounded-lg shadow-xl w-full max-w-3xl my-6 mx-4 max-h-[90vh] flex flex-col'>
        { /* Dialog Header */ }
        <div className='flex justify-between items-center p-4 border-b border-gray-200'>
          <h2 className='text-xl font-semibold text-gray-800'>
            Panelist Notes
          </h2>
          <button
              onClick={handleClosePanelistNotes}
              className='text-gray-500 hover:text-gray-700 focus:outline-none'
          >
            <span className='text-2xl'>&times;</span>
          </button>
        </div>

        { /* Dialog Content */ }
        <div className='flex-1 overflow-auto p-6 space-y-4'>
          { /* Interviewee Info */ }
          <div className='p-3 bg-gray-50 rounded-lg border border-gray-200'>
            <NameEmailAvatar
                avatar=''
                email={email || 'Unknown Email'}
                name={intervieweeName || 'Unknown Candidate'}
            />
          </div>

          { /* Display all panelist notes, allowing current panelist to edit their own */ }
          <div className='space-y-2 max-h-[400px] overflow-auto'>
            { existingNotes.map((note, index) => {
              const panelistId = (note.panelist || '').trim();
              const isCurrentUserNote =
                isPanelist && panelistId === userPanelistId;
              const isEditing =
                isCurrentUserNote && editingNoteId === note.id && expanded;

              const panelistInfo = panelistsMap[panelistId];
              const panelistName = panelistInfo
                ? `${panelistInfo.firstName || ''} ${
                  panelistInfo.lastName || ''
                }`.trim()
                : 'Unknown Panelist';

              return (
                <div key={index} className='  '>
                  <div className='flex justify-between items-start mb-2'>
                    <div className='flex-1'>
                      <NameEmailAvatar
                          avatar={panelistInfo?.avatar || ''}
                          avatarHeight='32px'
                          avatarWidth='32px'
                          email={panelistInfo?.email || ''}
                          name={panelistName}
                      />
                      { note.createdAt && (
                        <div className='ml-10 mt-1 text-xs text-gray-500'>
                          { new Date(note.createdAt).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            },
                          ) }
                        </div>
                      ) }
                    </div>

                    { /* Only show Edit button if this is the current user's note and not currently editing */ }
                    { isCurrentUserNote && !isEditing && (
                      <Button
                          variant='link'
                          label='Edit'
                          onClick={() => {
                            setEditingNoteId(note.id);
                            setEditingNoteText(note.notes);
                            setExpanded(true);
                          }}
                      />
                    ) }
                  </div>

                  { /* Show edit form if this is the user's note and they're editing it */ }
                  { isEditing ? (
                    <div className='space-y-2 mt-2 px-2'>
                      <textarea
                          className='w-full h-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none resize-none'
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          placeholder='Edit your note...'
                          disabled={loading}
                      />
                      <div className='flex justify-end gap-2'>
                        <Button
                            variant='secondary'
                            label='Cancel'
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditingNoteText('');
                              setExpanded(false);
                            }}
                        />
                        <Button
                            variant='primary'
                            label='Save'
                            onClick={handleSave}
                            disabled={
                              loading ||
                            !editingNoteText.trim() ||
                            editingNoteText.trim() === note.notes.trim()
                            }
                        />
                      </div>
                    </div>
                  ) : (
                    // Show the note content
                    <div className='ml-10 mt-2  text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 p-2 rounded'>
                      { note.notes }
                    </div>
                  ) }
                </div>
              );
            }) }

            { /* Add New Note Section - only show if user doesn't already have a note or there are no notes yet */ }
            { isPanelist &&
              !existingNotes.some(
                (note) => (note.panelist || '').trim() === userPanelistId,
              ) && (
              <>
                { !expanded ? (
                  <div className='mt-4'>
                    <Button
                        hasIcon
                        icon={<ChevronDown />}
                        variant='secondary'
                        onClick={() => {
                          setEditingNoteId(null);
                          setExpanded(true);
                        }}
                        label='Add New Note'
                    />
                  </div>
                ) : (
                  <div className='space-y-2 mt-4 '>
                    <div className='flex justify-between items-center'>
                      <label className='text-sm font-medium text-gray-700'>
                        New Note
                      </label>
                      <Button
                          hasIcon
                          icon={<ChevronUp />}
                          variant='link'
                          onClick={() => setExpanded(false)}
                          label='Hide'
                      />
                    </div>
                    <textarea
                        className='w-full h-32 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none resize-none'
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder='Write your feedback, impressions, or notes from the interview...'
                        disabled={loading}
                    />
                    { error && <p className='text-red-500 text-sm'>{ error }</p> }
                    <div className='flex justify-end gap-2'>
                      <Button
                          variant='secondary'
                          label='Cancel'
                          onClick={() => setExpanded(false)}
                      />
                      <Button
                          variant='primary'
                          label='Save'
                          onClick={handleSave}
                          disabled={loading || !newNote.trim()}
                      />
                    </div>
                  </div>
                ) }
              </>
            ) }

            { /* No Notes Message */ }
            { existingNotes.length === 0 && !isPanelist && (
              <p className='text-gray-600 text-sm text-center py-4'>
                No notes available.
              </p>
            ) }
          </div>
        </div>
      </div>
    </div>
  );
};
